import { useEffect, useRef, useState } from 'react';
import { type AsyncRateLimiterState, useAsyncRateLimiter } from '@tanstack/react-pacer';
import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { milliseconds } from 'date-fns';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import { logger } from '@/lib/logger';

const DEFAULT_LIMIT = 2;
const DEFAULT_WINDOW_MS = milliseconds({ seconds: 30 });

interface RateLimitedResendConfig<TParams, TData> {
  fetcher: (params: TParams) => Promise<TData>;
  errorMessage: string;
  /** sessionStorage key + devtools label for the persisted limiter state. */
  storeName: string;
  limit?: number;
  windowMs?: number;
}

/**
 * "Send + resend with cooldown" mutation hook: Pacer rate-limits and persists state
 * to sessionStorage (survives remounts), exposing `canResend` + `remainingMs` for a
 * countdown. UX guardrail only — real abuse protection is in the WAF.
 */
export function createRateLimitedResendMutation<TParams, TData>({
  fetcher,
  errorMessage,
  storeName,
  limit = DEFAULT_LIMIT,
  windowMs = DEFAULT_WINDOW_MS,
}: RateLimitedResendConfig<TParams, TData>) {
  type RateLimiterState = AsyncRateLimiterState<(params: TParams) => Promise<TData>>;

  interface RateLimiterStore {
    state: RateLimiterState | null;
    setState: (state: RateLimiterState) => void;
    resetState: () => void;
  }

  const useRateLimiterStore = create<RateLimiterStore>()(
    devtools(
      persist(
        (set) => ({
          state: null,
          setState: (state) => set({ state }),
          resetState: () => set({ state: null }),
        }),
        {
          name: `${storeName}-storage`,
          storage: createJSONStorage(() => sessionStorage),
        }
      ),
      {
        name: storeName,
        enabled: import.meta.env.MODE !== 'test',
      }
    )
  );

  return function useRateLimitedResendMutation(
    options?: UseMutationOptions<TData, Error, TParams>
  ) {
    const intervalRef = useRef<number | null>(null);
    const [, forceUpdate] = useState({});

    const asyncRateLimiter = useAsyncRateLimiter(
      fetcher,
      {
        onError: (error) => {
          logger.error(errorMessage, error);
        },
        limit,
        window: windowMs,
        windowType: 'fixed',
        initialState: useRateLimiterStore.getState().state ?? undefined,
      },
      (state) => state
    );

    useEffect(
      function persistState() {
        useRateLimiterStore.getState().setState(asyncRateLimiter.state);
      },
      [asyncRateLimiter.state]
    );

    const remainingMs = asyncRateLimiter.getMsUntilNextWindow();

    const reset = () => {
      asyncRateLimiter.reset();
      useRateLimiterStore.getState().resetState();
    };

    useEffect(
      function resetExpiredRateLimiter() {
        if (asyncRateLimiter.state.isExceeded && remainingMs <= 0) {
          asyncRateLimiter.reset();
          useRateLimiterStore.getState().resetState();
        }
      },
      [asyncRateLimiter, remainingMs]
    );

    const canResend = !asyncRateLimiter.state.isExceeded;

    useEffect(
      function tickCountdownWhileCoolingDown() {
        if (!canResend) {
          // Pacer's remaining time isn't reactive; tick a re-render to update the label.
          intervalRef.current = window.setInterval(
            () => {
              forceUpdate({});
            },
            milliseconds({ seconds: 1 })
          );
        } else if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        return () => {
          if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
          }
        };
      },
      [canResend]
    );

    const mutation = useMutation({
      mutationFn: async (params: TParams) => {
        const result = await asyncRateLimiter.maybeExecute(params);

        if (result === undefined) {
          if (asyncRateLimiter.state.isExceeded) {
            throw new Error('Rate limit exceeded');
          }
          throw new Error('Failed to send verification code. Please try again.');
        }

        return result;
      },
      ...options,
    });

    return {
      ...mutation,
      remainingMs,
      canResend,
      reset,
    };
  };
}
